<template>
  <v-content class="profile page">
    <can I="read" a="Profile">
      <v-container v-if="profile">
        <v-toolbar flat class="transparent">
          <v-breadcrumbs
            class="pl-0"
            :items="[
              { exact: true, text: 'Admin', to: '/dashboard' },
              { exact: true, text: 'Profiles', to: '/profiles' },
              { exact: true, text: $route.params.id, to: `/profiles/${$route.params.id}` },
            ]"
          >
            <v-icon slot="divider">mdi-chevron-right</v-icon>
          </v-breadcrumbs>

          <v-spacer />

          <v-menu bottom>
            <v-btn flat fab small slot="activator" color="red">
              <v-icon>mdi-file-pdf</v-icon>
            </v-btn>
            <v-list dense one-line>
              <v-list-tile @click="download">
                <v-list-tile-avatar>
                  <v-icon v-if="downloading">mdi-loading</v-icon>
                  <v-icon v-else color="red">mdi-file-pdf</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content><span>Download PDF</span></v-list-tile-content>
              </v-list-tile>
              <can I="regenerate" a="Profile">
                <v-list-tile @click="regenerateDocument">
                  <v-list-tile-avatar>
                    <v-icon v-if="regenerating">mdi-loading</v-icon>
                    <v-icon v-else>mdi-refresh</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content><span>Regenerate PDF</span></v-list-tile-content>
                </v-list-tile>
              </can>
              <v-divider />
              <v-subheader>Preview</v-subheader>
              <v-list-tile :href="profile.sections.profile">
                <v-list-tile-avatar>
                  <v-icon v-if="profile.sections.profile" color="green">mdi-check</v-icon>
                  <v-icon v-else color="red">mdi-alert-circle-outline</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>Profile Section</v-list-tile-content>
              </v-list-tile>
              <v-list-tile :href="profile.sections.identity">
                <v-list-tile-avatar>
                  <v-icon v-if="profile.sections.identity" color="green">mdi-check</v-icon>
                  <v-icon v-else color="red">mdi-alert-circle-outline</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>Proof of Identity Section</v-list-tile-content>
              </v-list-tile>
              <v-list-tile :href="profile.sections.transcript">
                <v-list-tile-avatar>
                  <v-icon v-if="profile.sections.transcript" color="green">mdi-check</v-icon>
                  <v-icon v-else color="red">mdi-alert-circle-outline</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>Transcripts Section</v-list-tile-content>
              </v-list-tile>
              <v-list-tile :href="profile.sections.financial">
                <v-list-tile-avatar>
                  <v-icon v-if="profile.sections.financial" color="green">mdi-check</v-icon>
                  <v-icon v-else color="red">mdi-alert-circle-outline</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>Financial Survey Section</v-list-tile-content>
              </v-list-tile>
              <v-divider />
              <v-subheader>New pipeline preview</v-subheader>
              <v-list-tile :href="htmlProfileUrl" target="_blank">
                <v-list-tile-avatar>
                  <v-icon color="primary">mdi-code-tags</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>Preview HTML</v-list-tile-content>
              </v-list-tile>
              <v-list-tile :href="pdfProfileUrl" target="_blank">
                <v-list-tile-avatar>
                  <v-icon color="red">mdi-file-pdf</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>Preview PDF</v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-tooltip left>
            <v-btn small flat fab slot="activator" @click="refreshCompletion">
              <v-icon>mdi-account-convert</v-icon>
            </v-btn>
            <span>Recalculate Completion</span>
          </v-tooltip>
          <v-tooltip left>
            <v-btn small flat fab slot="activator" @click="refresh">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <span>Reload Profile</span>
          </v-tooltip>
          <CopyButton />
        </v-toolbar>

        <Edit v-if="editing" @refresh="refresh" @update="update" @cancel="cancel" />
        <Show v-else @refresh="refresh" @destroy="destroy" @edit="editing = true" />

        <can I="edit" a="Profile">
          <Languages />
          <WorkAndStudyRights />
          <Affiliations />
          <Achievements />
        </can>
        <can I="list" a="ProfileQualification">
          <ProfileQualifications
            :flags="
              profile.flags ? profile.flags.filter(f => f.targetType == 'ProfileQualification') : []
            "
          />
        </can>

        <v-toolbar class="transparent elevation-0 my-3">
          <div class="headline">Work Experience</div>
          <v-spacer />
          <can I="create" a="Experience">
            <v-btn round @click="addExperience" color="secondary">
              <v-icon left>mdi-plus</v-icon> Add
            </v-btn>
          </can>
        </v-toolbar>

        <can I="list" a="Experience">
          <Experience
            v-for="experience in experiences"
            :value="experience"
            :key="`experience-${experience.id}`"
            @cancel="cancel"
            @refresh="refresh"
          />
        </can>

        <can I="list" a="OpportunityApplication">
          <Applications :profile="profile" />
        </can>

        <Flags v-if="profile && profile.flags" :value="profile.flags" @refresh="refresh" />

        <FinancialAssessment v-if="profile" :profile="profile" @refresh="refresh" />
      </v-container>
    </can>
  </v-content>
</template>

<script>
// Components
import Achievements from '@/components/profiles/Achievements'
import Affiliations from '@/components/profiles/Affiliations'
import Applications from '@/components/profiles/Applications'
import Edit from '@/components/profiles/Edit'
import Experience from '@/components/profiles/Experience'
import Flags from '@/components/profiles/Flags'
import FinancialAssessment from '@/components/profiles/FinancialAssessment'
import Languages from '@/components/profiles/Languages'
import ProfileQualifications from '@/components/profiles/ProfileQualifications'
import Show from '@/components/profiles/Show'
import WorkAndStudyRights from '@/components/profiles/WorkAndStudyRights'

// Helpers
import { mapGetters, mapState } from 'vuex'
import { generatedDocumentDownloads } from '@/mixins/generatedDocumentDownloads.js'

// Vue Component
export default {
  mixins: [generatedDocumentDownloads],
  components: {
    Applications,
    Show,
    Edit,
    ProfileQualifications,
    Experience,
    Flags,
    FinancialAssessment,
    WorkAndStudyRights,
    Languages,
    Affiliations,
    Achievements,
  },
  created() {
    // Needed for this page
    this.$store.dispatch('admin/profiles/fetch', this.$route.params.id)
    this.$store.dispatch('admin/experiences/search', {
      filters: { profile: this.$route.params.id },
      sortBy: 'endsAt',
      descending: true,
    })
    this.$store.dispatch('admin/profileQualifications/search', {
      filters: { profile: this.$route.params.id },
      sortBy: 'endsAt',
      descending: true,
    })
    // Experience -> Organization Select
    this.$store.dispatch('organizations/fetchAll')
    // Experience -> Discipline Select
    this.$store.dispatch('disciplines/fetchAll')
    // Profile Qualification -> Qualification Selects
    this.$store.dispatch('institutions/fetchAll')
    this.$store.dispatch('qualificationCategories/fetchAll')
    this.$store.dispatch('qualificationTypes/fetchAll')
    this.$store.dispatch('qualifications/fetchAll')
  },
  data() {
    return {
      editing: false,
      downloading: false,
      regenerating: false,
    }
  },
  computed: {
    ...mapGetters({ profile: 'admin/profiles/find' }),
    ...mapState('admin/experiences', { experiences: 'active' }),
    htmlProfileUrl() {
      if (!this.profile) return null
      const token = (this.$auth && this.$auth.getToken && encodeURIComponent(this.$auth.getToken())) || ''
      return `${this.$root.$api || ''}/admin/html_documents/profile/${this.profile.id}?auth_token=${token}`
    },
    pdfProfileUrl() {
      if (!this.profile) return null
      const token = (this.$auth && this.$auth.getToken && encodeURIComponent(this.$auth.getToken())) || ''
      return `${this.$root.$api || ''}/admin/html_documents/profile/${this.profile.id}.pdf?auth_token=${token}`
    },
  },
  methods: {
    refreshCompletion() {
      this.$store.dispatch('admin/profiles/refreshCompletion', this.$route.params.id)
    },
    refresh() {
      this.$store.dispatch('admin/profiles/fetch', this.$route.params.id)
      this.$store.dispatch('admin/experiences/search', {
        filters: { profile: this.$route.params.id },
        sortBy: 'endsAt',
        descending: true,
      })
      this.$store.dispatch('admin/profileQualifications/search', {
        filters: { profile: this.profile.id },
        sortBy: 'endsAt',
        descending: true,
      })
    },
    cancel() {
      this.refresh()
      this.editing = false
    },
    update() {
      this.$store.dispatch('admin/profiles/update', this.profile).then(() => {
        if (this.profile.errors) {
          this.$store.commit('snackbar', {
            text: 'There was a problem updating the profile',
            type: 'error',
          })
        } else {
          this.$store.commit('snackbar', {
            text: 'Profile was updated successfully',
            type: 'success',
          })
          this.editing = false
        }
      })
    },
    destroy() {
      this.$store.dispatch('admin/profiles/destroy', this.$route.params.id).then(
        () => {
          // Success
          this.$store.commit('snackbar', {
            text: 'Profile was deleted successfully',
            type: 'success',
          })
          this.$router.push('/profiles')
        },
        () => {
          // Failure
          this.$store.commit('snackbar', {
            text: 'There was a problem deleting the profile',
            type: 'error',
          })
        }
      )
    },
    addExperience() {
      if (this.experiences.length == 0 || (this.experiences.length && this.experiences[0].id)) {
        this.experiences.unshift({
          id: null,
          position: '',
          location: { address: '' },
          startsAt: null,
          endsAt: null,
          profile: { id: this.$route.params.id },
          organization: { name: '', id: null },
          errors: null,
        })
      }
    },
    async download() {
      this.downloading = true
      const filename = `${this.profile.firstNames} ${this.profile.lastName}.pdf`

      await this.handleGeneratedDocumentRequest(
        this.$http.get(`/admin/profiles/${this.profile.id}.pdf`, { responseType: 'blob' }),
        {
          filename,
          pendingMessage: 'Your profile document has been requested. You will receive an email with a download link.',
          errorMessage: 'There was a problem downloading the profile document.',
        }
      )

      this.downloading = false
    },
    async regenerateDocument() {
      this.regenerating = true
      try {
        await this.$store.dispatch('admin/profiles/regenerateDocument', this.profile.id)
        this.$store.commit('snackbar', {
          text: 'Profile document regeneration requested.',
          type: 'success',
        })
      } catch (error) {
        this.$store.commit('snackbar', {
          text: 'There was a problem requesting profile document regeneration.',
          type: 'error',
        })
      }
      this.regenerating = false
    },
  },
}
</script>
